<template>
    <div class="bmv-table-wrap">
        <!-- Loading -->
        <div v-if="loading" class="bmv-table-loading">
            <div class="spinner-border text-primary" role="status">
                <span class="sr-only">{{ $t('forms.loading') }}...</span>
            </div>
        </div>

        <template v-else>
            <!-- Table -->
            <div class="bmv-table-container">
                <table class="bmv-table">
                    <thead>
                        <tr>
                            <th
                                v-for="col in columns"
                                :key="col.field"
                                class="bmv-th"
                                :class="{
                                    'bmv-th--center': col.align === 'center',
                                    'bmv-th--right': col.align === 'right',
                                    'bmv-th--sortable': col.sortable
                                }"
                                :style="col.width ? { width: col.width } : {}"
                                @click="col.sortable ? onSort(col.field) : null"
                            >
                                <span class="bmv-th-content">
                                    {{ col.label }}
                                    <i
                                        v-if="col.sortable"
                                        class="bmv-sort-icon fal"
                                        :class="getSortIcon(col.field)"
                                    ></i>
                                </span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="!data || data.length === 0">
                            <td :colspan="columns.length" class="bmv-empty">
                                <i class="fal fa-inbox"></i>
                                <span>{{ emptyText || $t('forms.empty') || 'Aucune donnée' }}</span>
                            </td>
                        </tr>
                        <tr
                            v-for="(row, rowIndex) in data"
                            :key="rowKey ? row[rowKey] : rowIndex"
                            class="bmv-row"
                            :class="{ 'bmv-row--clickable': rowClickable }"
                            @click="rowClickable ? $emit('row-click', row) : null"
                        >
                            <td
                                v-for="col in columns"
                                :key="col.field"
                                class="bmv-td"
                                :class="{
                                    'bmv-td--center': col.align === 'center',
                                    'bmv-td--right': col.align === 'right'
                                }"
                            >
                                <!-- Named slot for custom cell rendering -->
                                <slot :name="'cell-' + col.field" :row="row" :value="getNestedValue(row, col.field)">
                                    <!-- Default rendering by type -->
                                    <span v-if="col.type === 'id'" class="bmv-cell-id">#{{ getNestedValue(row, col.field) }}</span>
                                    <span v-else-if="col.type === 'badge'" class="bmv-cell-badge">{{ getNestedValue(row, col.field) }}</span>
                                    <span v-else-if="col.type === 'date'">{{ formatDate(getNestedValue(row, col.field)) }}</span>
                                    <span v-else-if="col.type === 'link'">
                                        <router-link :to="col.to ? col.to(row) : '#'" class="bmv-cell-link">
                                            {{ getNestedValue(row, col.field) }}
                                        </router-link>
                                    </span>
                                    <span v-else>{{ getNestedValue(row, col.field) }}</span>
                                </slot>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Pagination -->
            <div v-if="pagination && totalPages > 1" class="bmv-pagination">
                <div class="bmv-pagination-info">
                    {{ paginationStart }}–{{ paginationEnd }} sur {{ totalResults }}
                </div>
                <div class="bmv-pagination-nav">
                    <button
                        class="bmv-page-btn"
                        :disabled="currentPage <= 1"
                        @click="$emit('page-change', currentPage - 1)"
                    >
                        <i class="fal fa-chevron-left"></i>
                    </button>
                    <button
                        v-for="page in visiblePages"
                        :key="page"
                        class="bmv-page-btn"
                        :class="{ 'is-active': page === currentPage }"
                        @click="$emit('page-change', page)"
                    >
                        {{ page }}
                    </button>
                    <button
                        class="bmv-page-btn"
                        :disabled="currentPage >= totalPages"
                        @click="$emit('page-change', currentPage + 1)"
                    >
                        <i class="fal fa-chevron-right"></i>
                    </button>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
export default {
    name: 'BmvTable',
    props: {
        columns: {
            type: Array,
            required: true
            // { field, label, sortable, align, width, type }
        },
        data: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
            default: false
        },
        emptyText: {
            type: String,
            default: null
        },
        rowKey: {
            type: String,
            default: 'id'
        },
        rowClickable: {
            type: Boolean,
            default: false
        },
        // Sorting
        sortField: {
            type: String,
            default: null
        },
        sortDirection: {
            type: String,
            default: 'asc'
        },
        // Pagination
        pagination: {
            type: Boolean,
            default: false
        },
        totalResults: {
            type: Number,
            default: 0
        },
        totalPages: {
            type: Number,
            default: 0
        },
        currentPage: {
            type: Number,
            default: 1
        },
        perPage: {
            type: Number,
            default: 20
        }
    },
    computed: {
        paginationStart() {
            return (this.currentPage - 1) * this.perPage + 1
        },
        paginationEnd() {
            const end = this.currentPage * this.perPage
            return end > this.totalResults ? this.totalResults : end
        },
        visiblePages() {
            const maxButtons = 5
            let start = Math.max(1, this.currentPage - Math.floor(maxButtons / 2))
            let end = start + maxButtons - 1
            if (end > this.totalPages) {
                end = this.totalPages
                start = Math.max(1, end - maxButtons + 1)
            }
            const pages = []
            for (let i = start; i <= end; i++) pages.push(i)
            return pages
        }
    },
    methods: {
        getNestedValue(obj, path) {
            if (!path) return ''
            return path.split('.').reduce((o, k) => (o && o[k] !== undefined ? o[k] : ''), obj)
        },
        formatDate(val) {
            if (!val) return ''
            return this.moment ? this.moment(val).format('L') : new Date(val).toLocaleDateString()
        },
        getSortIcon(field) {
            if (this.sortField !== field) return 'fa-sort'
            return this.sortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
        },
        onSort(field) {
            let direction = 'asc'
            if (this.sortField === field) {
                direction = this.sortDirection === 'asc' ? 'desc' : 'asc'
            }
            this.$emit('sort', { field, direction })
        }
    }
}
</script>

<style scoped>
.bmv-table-wrap {
    background: #fff;
    border-radius: 14px;
    border: 1px solid #e5e7eb;
    overflow: hidden;
    margin: 16px;
}

.bmv-table-loading {
    display: flex;
    justify-content: center;
    padding: 48px 0;
}

.bmv-table-container {
    overflow-x: auto;
}

/* Table */
.bmv-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.84rem;
}

/* Header */
.bmv-th {
    padding: 12px 16px;
    text-align: left;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #64748b;
    background: #f8fafc;
    border-bottom: 2px solid #e5e7eb;
    white-space: nowrap;
    user-select: none;
}

.bmv-th--center {
    text-align: center;
}

.bmv-th--right {
    text-align: right;
}

.bmv-th--sortable {
    cursor: pointer;
    transition: color 0.15s;
}

.bmv-th--sortable:hover {
    color: #0891B2;
}

.bmv-th-content {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.bmv-sort-icon {
    font-size: 0.65rem;
    color: #cbd5e1;
}

.bmv-th--sortable:hover .bmv-sort-icon {
    color: #0891B2;
}

/* Body */
.bmv-row {
    transition: background 0.12s;
}

.bmv-row:hover {
    background: #f8fafc;
}

.bmv-row--clickable {
    cursor: pointer;
}

.bmv-td {
    padding: 12px 16px;
    border-bottom: 1px solid #f1f5f9;
    color: #334155;
    vertical-align: middle;
}

.bmv-td--center {
    text-align: center;
}

.bmv-td--right {
    text-align: right;
}

/* Cell types */
.bmv-cell-id {
    font-size: 0.78rem;
    font-weight: 600;
    color: #94a3b8;
}

.bmv-cell-badge {
    display: inline-flex;
    padding: 3px 10px;
    background: #eff6ff;
    color: #3b82f6;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
    white-space: nowrap;
}

.bmv-cell-link {
    color: #0891B2;
    font-weight: 500;
    text-decoration: none;
    transition: color 0.15s;
}

.bmv-cell-link:hover {
    color: #0e7490;
    text-decoration: underline;
}

/* Empty state */
.bmv-empty {
    padding: 48px 16px;
    text-align: center;
    color: #94a3b8;
}

.bmv-empty i {
    display: block;
    font-size: 2rem;
    margin-bottom: 10px;
}

.bmv-empty span {
    font-size: 0.88rem;
}

/* Pagination */
.bmv-pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-top: 1px solid #f1f5f9;
}

.bmv-pagination-info {
    font-size: 0.78rem;
    color: #94a3b8;
}

.bmv-pagination-nav {
    display: flex;
    align-items: center;
    gap: 4px;
}

.bmv-page-btn {
    min-width: 32px;
    height: 32px;
    padding: 0 8px;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    background: #fff;
    color: #64748b;
    font-size: 0.78rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.15s;
}

.bmv-page-btn:hover:not(:disabled):not(.is-active) {
    border-color: #0891B2;
    color: #0891B2;
    background: rgba(8, 145, 178, 0.04);
}

.bmv-page-btn.is-active {
    background: #0891B2;
    border-color: #0891B2;
    color: #fff;
}

.bmv-page-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.bmv-page-btn i {
    font-size: 0.72rem;
}
</style>
